import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Check, CircleDashed, Loader2 } from "lucide-react";
import { z } from "zod";
import { apiRequest, parseJsonResponse } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const handoffSchema = z.object({
  runId: z.string().nullable(),
  idempotencyKey: z.string().nullable(),
  humanReviewRequired: z.literal(true),
  admissionThresholdOnly: z.literal(true),
  inFlightMayExceedThreshold: z.literal(true),
  enrichmentComplete: z.boolean(),
  selectedPersonaIds: z.array(z.string()),
  frozen: z.boolean(),
  recipe: z.record(z.unknown()).nullable(),
});
type HandoffSnapshot = z.infer<typeof handoffSchema>;
const handoffUrl = (id: string) => `/api/admin/smb/orders/${encodeURIComponent(id)}/handoff`;

function safeHandoffError(error: unknown) {
  const message = error instanceof Error ? error.message : "";
  const status = Number(message.match(/(?:^|\s)(\d{3})(?:\s|:)/)?.[1] ?? 0);
  if (status === 404) return "Handoff inspection is not available for this order yet.";
  if (status === 403) return "You do not have access to inspect this handoff.";
  return "The handoff snapshot could not be loaded. Refresh to try again.";
}

function prettyRecipe(recipe: HandoffSnapshot["recipe"]) {
  if (!recipe) return "No recipe has been recorded.";
  try { return JSON.stringify(recipe, null, 2); } catch { return "The recipe could not be displayed."; }
}

export function HandoffInspection({ orderId, busy, onRun }: {
  orderId: string;
  busy: boolean;
  onRun(key: string, dryRun: boolean): void;
}) {
  const query = useQuery<HandoffSnapshot>({
    queryKey: [handoffUrl(orderId)],
    queryFn: async () => handoffSchema.parse(await parseJsonResponse(await apiRequest("GET", handoffUrl(orderId)))),
    refetchInterval: busy ? 2000 : false,
  });

  if (query.isLoading) return <div className="mb-4 space-y-2" aria-label="Loading handoff snapshot">
    <div className="h-12 animate-pulse rounded-lg bg-muted" /><div className="h-24 animate-pulse rounded-lg bg-muted/70" />
  </div>;
  if (query.isError || !query.data) return <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-xs text-destructive" role="alert">
    <p className="font-medium">Handoff snapshot unavailable</p><p className="mt-1">{safeHandoffError(query.error)}</p>
    <Button size="sm" variant="outline" className="mt-3" onClick={() => void query.refetch()}>Refresh handoff</Button>
  </div>;

  const handoff = query.data;
  const runAction = (key: string, dryRun: boolean) => {
    onRun(key, dryRun);
    window.setTimeout(() => void query.refetch(), 900);
  };
  return <section className="mb-4 overflow-hidden rounded-xl border bg-card" data-testid="smb-handoff-inspection">
    <div className="flex flex-wrap items-start justify-between gap-3 border-b px-4 py-3">
      <div><p className="text-[10px] font-semibold uppercase tracking-[.14em] text-muted-foreground">Handoff inspection</p>
        <p className="mt-1 text-xs text-muted-foreground">Read-only view of the intended package run. Inspection has no side effects.</p></div>
      {handoff.frozen
        ? <Badge variant="default" className="gap-1 text-[10px]"><Check className="h-3 w-3" />Frozen</Badge>
        : <Badge variant="secondary" className="gap-1 text-[10px]"><CircleDashed className="h-3 w-3" />Not frozen</Badge>}
    </div>
    <div className="grid gap-3 px-4 py-3 sm:grid-cols-2">
      <div className="min-w-0"><p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Run ID</p><p className="mt-1 break-all font-mono text-[11px]">{handoff.runId ?? "Not assigned"}</p></div>
      <div className="min-w-0"><p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Idempotency key</p><p className="mt-1 break-all font-mono text-[11px]">{handoff.idempotencyKey ?? "Not assigned"}</p></div>
    </div>
    <div className="grid grid-cols-2 gap-2 px-4 pb-3 sm:grid-cols-3">
      <div className="rounded-lg border bg-muted/30 px-3 py-2"><p className="text-[10px] text-muted-foreground">Enrichment</p><p className="mt-1 flex items-center gap-1 text-xs font-medium">{handoff.enrichmentComplete ? <Check className="h-3.5 w-3.5 text-primary" /> : <AlertTriangle className="h-3.5 w-3.5 text-muted-foreground" />}{handoff.enrichmentComplete ? "Complete" : "Incomplete"}</p></div>
      <div className="rounded-lg border bg-muted/30 px-3 py-2"><p className="text-[10px] text-muted-foreground">Frozen personas</p><p className="mt-1 text-xs font-medium tabular-nums">{handoff.selectedPersonaIds.length} selected</p></div>
      <div className="col-span-2 rounded-lg border bg-muted/30 px-3 py-2 sm:col-span-1"><p className="text-[10px] text-muted-foreground">Review policy</p><p className="mt-1 text-xs font-medium">Human review required</p></div>
    </div>
    <div className="px-4 pb-3">
      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Selected persona IDs</p>
      {handoff.selectedPersonaIds.length
        ? <ul className="space-y-1">{handoff.selectedPersonaIds.map(id => <li key={id} className="break-all rounded-md border bg-muted/30 px-2 py-1 font-mono text-[10px]">{id}</li>)}</ul>
        : <p className="text-xs text-muted-foreground">No personas selected.</p>}
    </div>
    <div className="px-4 pb-4">
      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Intended / frozen recipe</p>
      <pre className="max-h-72 overflow-auto rounded-lg border bg-muted/30 p-3 font-mono text-[10px] leading-relaxed text-foreground">{prettyRecipe(handoff.recipe)}</pre>
    </div>
    <div className="flex flex-wrap gap-2 border-t bg-muted/20 px-4 py-3">
      <Button size="sm" variant="outline" disabled={busy} onClick={() => runAction("handoff.attach_records", true)}>
        {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}Preview record attachment
      </Button>
      <Button size="sm" disabled={busy} onClick={() => runAction("handoff.start_run", false)}>
        {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}Start intended run
      </Button>
    </div>
  </section>;
}
